import { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { estimateResolvedLength, hasPlaceholders } from '../placeholderEstimate';
import { PlaceholderHint } from './PlaceholderHint';

// Meta descriptions. Spec §2.4: 0-3 entries (optional), single-sentence,
// wildcards. Chunk F amendment: authored length uses the same soft-warn
// pattern as headlines -- 255 is the sanity cap, no feed-truncation target
// since descriptions render differently across placements.
const MAX_ENTRIES = 3;
const SANITY_CAP = 255;

export type DescriptionArrayEditorProps = {
  values: string[];
  onChange: (next: string[]) => void;
};

export function DescriptionArrayEditor({ values, onChange }: DescriptionArrayEditorProps) {
  const [justCopied, setJustCopied] = useState<number | null>(null);

  const updateAt = (index: number, value: string) => {
    const next = [...values];
    next[index] = value;
    onChange(next);
  };
  const removeAt = (index: number) => onChange(values.filter((_, i) => i !== index));
  const addEntry = () => {
    if (values.length >= MAX_ENTRIES) return;
    onChange([...values, '']);
  };
  const copy = async (index: number) => {
    const text = values[index] ?? '';
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setJustCopied(index);
      window.setTimeout(() => setJustCopied((c) => (c === index ? null : c)), 1200);
    } catch {
      // See BodyCopyArrayEditor.
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between">
        <label className="text-sm font-medium text-slate-900">Descriptions</label>
        <span className="text-xs text-slate-500">
          {values.length} / {MAX_ENTRIES} entries (optional)
        </span>
      </div>

      {values.map((value, index) => {
        const authored = value.length;
        const estimated = estimateResolvedLength(value);
        const showEstimate = hasPlaceholders(value);
        const overSanity = authored > SANITY_CAP;
        return (
          <div key={index} className="flex items-start gap-2">
            <div className="flex-1 space-y-1">
              <input
                type="text"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                value={value}
                onChange={(e) => updateAt(index, e.target.value)}
                placeholder={`Description ${index + 1}`}
              />
              <div className={`text-xs ${overSanity ? 'text-red-600' : 'text-slate-500'}`}>
                {authored} chars
                {showEstimate && ` (~${estimated} resolved)`}
                {overSanity ? ` — over sanity cap ${SANITY_CAP}, will reject on save` : ''}
              </div>
            </div>
            <div className="flex flex-col gap-1 pt-1">
              <button
                type="button"
                className="text-xs text-slate-500 hover:text-slate-900 disabled:opacity-50"
                onClick={() => copy(index)}
                disabled={!value}
              >
                {justCopied === index ? 'Copied' : 'Copy'}
              </button>
              <button
                type="button"
                className="text-xs text-red-600 hover:text-red-800"
                onClick={() => removeAt(index)}
              >
                Remove
              </button>
            </div>
          </div>
        );
      })}

      <div className="flex justify-between items-center">
        <PlaceholderHint />
        <Button
          type="button"
          variant="outline"
          onClick={addEntry}
          disabled={values.length >= MAX_ENTRIES}
        >
          Add description
        </Button>
      </div>
    </div>
  );
}
